/**
 * 基础组件：按钮、表单项、开关、卡片、徽章、提示条、数字卡、空状态、加载中。
 *
 * 背景：控制台与管理后台共用（PRD 7 章 packages/ui），样式见 styles.css。
 * 设计意图：组件只是带 class 的薄封装，不引入组件库；按钮自带 loading 状态，提交期间自动禁用，避免重复提交。
 * 关键约束：图标一律用 @tabler/icons-react，用 size 定尺寸；不用 emoji。
 */
import { IconAlertTriangle, IconCircleCheck, IconInfoCircle, IconPlugConnectedX } from '@tabler/icons-react'
import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react'
import { C } from '@ap/i18n/common'
import { useI18n } from '@ap/i18n'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: 'md' | 'sm'
  loading?: boolean
  icon?: ReactNode
  block?: boolean
  iconOnly?: boolean
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  block,
  iconOnly,
  className = '',
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const cls = [
    'ap-btn',
    variant !== 'secondary' && `ap-btn--${variant}`,
    size === 'sm' && 'ap-btn--sm',
    block && 'ap-btn--block',
    iconOnly && 'ap-btn--icon',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <button type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <span className="ap-spinner" /> : icon}
      {children}
    </button>
  )
}

/**
 * 表单：回车提交，但输入法选词时的回车不算提交。
 * 韩文、中文输入法组合中按回车是确认候选词；Safari 等浏览器会把它当成表单的隐式提交，
 * 这里在 keydown 阶段拦下（isComposing 或 keyCode 229）。
 */
export function Form({
  onSubmit,
  children,
  className = 'ap-form',
}: {
  onSubmit: () => void | Promise<void>
  children: ReactNode
  className?: string
}) {
  return (
    <form
      className={className}
      noValidate={false}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && (e.nativeEvent.isComposing || e.keyCode === 229)) e.preventDefault()
      }}
      onSubmit={(e) => {
        e.preventDefault()
        void onSubmit()
      }}
    >
      {children}
    </form>
  )
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  children: ReactNode
}) {
  return (
    <label className="ap-field">
      {label && <span className="ap-field__label">{label}</span>}
      {children}
      {error ? <span className="ap-field__error">{error}</span> : hint && <span className="ap-field__hint">{hint}</span>}
    </label>
  )
}

export function Input({ mono, className = '', ...rest }: InputHTMLAttributes<HTMLInputElement> & { mono?: boolean }) {
  return <input className={`ap-input ${mono ? 'ap-input--mono' : ''} ${className}`} {...rest} />
}

export function Textarea({ className = '', ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={`ap-textarea ${className}`} {...rest} />
}

export function Select({
  options,
  className = '',
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { options: { value: string; label: string }[] }) {
  return (
    <select className={`ap-select ${className}`} {...rest}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  )
}

export function Switch({
  checked,
  onChange,
  label,
  disabled,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  label?: ReactNode
  disabled?: boolean
}) {
  return (
    <label className="ap-switch">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  )
}

export function Card({
  title,
  actions,
  flush,
  children,
  className = '',
}: {
  title?: ReactNode
  actions?: ReactNode
  flush?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`ap-card ${className}`}>
      {(title || actions) && (
        <header className="ap-card__head">
          <div className="ap-card__title">{title}</div>
          {actions && <div className="ap-row">{actions}</div>}
        </header>
      )}
      <div className={`ap-card__body ${flush ? 'ap-card__body--flush' : ''}`}>{children}</div>
    </section>
  )
}

export type Tone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger'

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`ap-badge ${tone !== 'neutral' ? `ap-badge--${tone}` : ''}`}>{children}</span>
}

export function Notice({ tone = 'accent', children }: { tone?: Exclude<Tone, 'neutral'>; children: ReactNode }) {
  const icon =
    tone === 'success' ? <IconCircleCheck size={18} /> : tone === 'accent' ? <IconInfoCircle size={18} /> : <IconAlertTriangle size={18} />
  return (
    <div className={`ap-notice ap-notice--${tone}`} role={tone === 'danger' ? 'alert' : undefined}>
      {icon}
      <div>{children}</div>
    </div>
  )
}

export function Stat({ label, value, sub }: { label: ReactNode; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="ap-stat">
      <span className="ap-stat__label">{label}</span>
      <span className="ap-stat__value">{value}</span>
      {sub && <span className="ap-stat__sub">{sub}</span>}
    </div>
  )
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title?: ReactNode; children?: ReactNode }) {
  const { t } = useI18n()
  return (
    <div className="ap-empty">
      {icon}
      <div className="ap-empty__title">{title ?? t(C.noData)}</div>
      {children}
    </div>
  )
}

export function Loading({ label }: { label?: ReactNode }) {
  const { t } = useI18n()
  return (
    <div className="ap-loading">
      <span className="ap-spinner" />
      {label ?? t(C.loading)}
    </div>
  )
}

/**
 * 后端连不上时的整页提示（启动时 /me 返回网络错误或 5xx）。
 *
 * 背景：此前这种情况直接当成未登录送回登录页，后端一重启运营就要重新输密码和动态码。
 * 约束：只负责展示；自动重试由 useOfflineRetry 做，按钮只是让用户不用等下一次退避。
 */
export function Offline({ onRetry }: { onRetry: () => void }) {
  const { t } = useI18n()
  return (
    <div className="ap-offline" role="status">
      <IconPlugConnectedX size={32} />
      <strong>{t(C.offlineTitle)}</strong>
      <p>{t(C.offlineHint)}</p>
      <div className="ap-offline__retry">
        <span className="ap-spinner" />
        <Button size="sm" variant="secondary" onClick={onRetry}>
          {t(C.retry)}
        </Button>
      </div>
    </div>
  )
}

export function PageHead({ title, desc, actions }: { title: ReactNode; desc?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="ap-page-head">
      <div>
        <h1>{title}</h1>
        {desc && <p>{desc}</p>}
      </div>
      {actions && <div className="ap-row">{actions}</div>}
    </div>
  )
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
}: {
  items: { id: T; label: ReactNode }[]
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="ap-tabs" role="tablist">
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          role="tab"
          aria-selected={value === it.id}
          className={`ap-tab ${value === it.id ? 'ap-tab--active' : ''}`}
          onClick={() => onChange(it.id)}
        >
          {it.label}
        </button>
      ))}
    </div>
  )
}
